import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import message from 'antd/lib/message';
import { union } from 'lodash';

import { gqlSchema } from '../../gql/schema';
import PageWithTitle from '../../shared/PageWithTitle';
import { GQL_ClassResponse } from '../../types/class';
import ClassInfos from './ClassInfos';
import ActiveInvestigationsRow from '../../shared/StudentDashboardRows/ActiveInvestigationsRow';
import { DASHBOARD_TYPES, GQL_InvestigationDashboard } from '../../types/investigation';
import FinishedInvestigationsRow from '../../shared/StudentDashboardRows/FinishedInvestigationsRow';
import TeacherAnnouncementsRow from '../../shared/StudentDashboardRows/TeacherAnnouncementsRow';
//import { finishedInvestigationFilter } from '../../utils/investigation';
import { GQL_ClassesSubjects } from '../../types/class';
import FinishedAssessmentsRow from '../../shared/StudentDashboardRows/FinishedAssessmentsRow';
import ActiveAssessmentsRow from '../../shared/StudentDashboardRows/ActiveAssessmentsRow';
import { SUBJECT_TYPES } from '../../types/subjects';
import { Pagination } from 'antd';
//import { Pagination } from 'antd';

type Props = RouteComponentProps<{ id: string }, any, { className: string }>;

const StudentClassDetail: React.FC<Props> = (props) => {
  const { id } = props.match.params;
  const className = props.location.state?.className;
  const [hasNonEnterpriseTeacher, setHasNonEnterpriseTeacher] = useState(true);

  const pageSize = 3;
  const [pageActiveInv, setPageActiveInv] = useState(1);
  const [pageFinishedInv, setPageFinishedInv] = useState(1);
  const [pageActiveAssess, setPageActiveAssess] = useState(1);
  const [pageFinishedAssess, setPageFinishedAssess] = useState(1);

  const { data, loading } = useQuery<{ getClass: GQL_ClassResponse }, { data: { classId: string } }>(
    gqlSchema.ClassSchema.query.CLASS.CLASSES.getClass,
    {
      variables: { data: { classId: id } },
      onError: (err) => {
        message.error('There was an error loading the class: ' + err.message || 'Unexpected Error');
      },
    },
  );

  const { data: activeInvestigations, loading: loadingInvestigations, error: errorInvestigations } = useQuery<{
    getInvestigationsByStudentUser: GQL_InvestigationDashboard[];
  }>(gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByStudentUser, {
    variables: {
      filter:
      {
        classId: id,
        page: pageActiveInv,
        pageSize,
        type: DASHBOARD_TYPES.ACTIVE_INVESTIGATIONS
      }
    },
    onError: (error) => {
      message.error(
        error.message || 'There was an error loading your investigations, check you connection and try again later.',
      );
    },
  });

  const { data: finishedInvestigations } = useQuery<{
    getInvestigationsByStudentUser: GQL_InvestigationDashboard[];
  }>(gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByStudentUser, {
    variables: {
      filter:
      {
        classId: id,
        page: pageFinishedInv,
        pageSize,
        type: DASHBOARD_TYPES.FINISHED_INVESTIGATIONS
      }
    },
    onError: (error) => {
      message.error(
        error.message || 'There was an error loading your investigations, check you connection and try again later.',
      );
    },
  });

  const { data: finishedAssessments } = useQuery<{
    getInvestigationsByStudentUser: GQL_InvestigationDashboard[];
  }>(gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByStudentUser, {
    variables: {
      filter:
      {
        classId: id,
        page: pageFinishedAssess,
        pageSize,
        type: DASHBOARD_TYPES.FINISHED_ASSESSMENTS
      }
    },
    onError: (error) => {
      message.error(
        error.message || 'There was an error loading your investigations, check you connection and try again later.',
      );
    },
  });

  const { data: activeAssessments } = useQuery<{
    getInvestigationsByStudentUser: GQL_InvestigationDashboard[];
  }>(gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByStudentUser, {
    variables: {
      filter:
      {
        classId: id,
        page: pageActiveAssess,
        pageSize,
        type: DASHBOARD_TYPES.ACTIVE_ASSESSMENTS
      }
    },
    onError: (error) => {
      message.error(
        error.message || 'There was an error loading your investigations, check you connection and try again later.',
      );
    },
  });

  const { data: classesSubjects } = useQuery<{
    getClassesSubjects: GQL_ClassesSubjects[];
  }>(gqlSchema.ClassSchema.query.CLASS.CLASSES.getClassesSubjects, {
    onCompleted: (classesSubjects) => {
      if (classesSubjects?.getClassesSubjects) {
        const hasNonEnterpriseTeacher = classesSubjects?.getClassesSubjects.some(
          (each: GQL_ClassesSubjects) => each.isEnterpriseTeacher === false,
        );
        setHasNonEnterpriseTeacher(hasNonEnterpriseTeacher);
      }
    },
    onError: (error) => {
      message.error(
        error.message || 'There was an error loading your classes subjects, check you connection and try again later.',
      );
    },
  });

  const hasTexasSubjectEnabled = useMemo(() => {
    let hasTexasSubjectEnabled = false;

    const allSubjects = classesSubjects?.getClassesSubjects.map(
      (each: GQL_ClassesSubjects) => each.teacherAllowedSubjects || [],
    );

    if (allSubjects?.length) {
      const allSubjectsMerged = union(...allSubjects);
      hasTexasSubjectEnabled = allSubjectsMerged.some((each) => each === SUBJECT_TYPES.SCIENCE_TEXAS_EDITION);
    }

    return hasTexasSubjectEnabled;
  }, [classesSubjects]);

  const fetchInvestigations = useQuery<{
    getInvestigationsByStudentUser: GQL_InvestigationDashboard[];
  }>(gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationsByStudentUser, {
    skip: true, // Prevent automatic query execution on page load
    onError: (error) => {
      message.error(
        error.message || 'There was an error loading your investigations, check your connection and try again later.',
      );
    },
  });

  const handlePageChangeFinishedInv = async (newPage: number, type: DASHBOARD_TYPES) => {
    try {
      const { data } = await fetchInvestigations.refetch({
        filter: { page: newPage, pageSize, type, classId: id, },
      });

      const nextPageData = data?.getInvestigationsByStudentUser || [];

      if (nextPageData.length === 0) {
        message.info(`No more data available, returning to the previous page.`);
        return;
      }

  
      switch (type) {
        case DASHBOARD_TYPES.ACTIVE_INVESTIGATIONS:
          setPageActiveInv(newPage);
          break;
        case DASHBOARD_TYPES.FINISHED_INVESTIGATIONS:
          setPageFinishedInv(newPage);
          break;
        case DASHBOARD_TYPES.ACTIVE_ASSESSMENTS:
          setPageActiveAssess(newPage);
          break;
        case DASHBOARD_TYPES.FINISHED_ASSESSMENTS:
          setPageFinishedAssess(newPage);
          break;
        default:
          break;
      }
    } catch (error) {
      message.error('Failed to load data. Please try again.');
    }
  };


  const totalInvestigationsActiveInv = activeInvestigations?.getInvestigationsByStudentUser?.length || 0;
  const hasMorePagesActiveInv = totalInvestigationsActiveInv >= 3;

  const totalInvestigationsFinishedInv = finishedInvestigations?.getInvestigationsByStudentUser?.length || 0;
  const hasMorePagesFinishedInv = totalInvestigationsFinishedInv >= 3;

  const totalInvestigationsActiveAssessment = activeAssessments?.getInvestigationsByStudentUser?.length || 0;
  const hasMorePagesActiveAssessment = totalInvestigationsActiveAssessment >= 3;

  const totalInvestigationsFinishedAssessment = finishedAssessments?.getInvestigationsByStudentUser?.length || 0;
  const hasMorePagesFinishedAssessment = totalInvestigationsFinishedAssessment >= 3;




  return (
    <>
      <PageWithTitle title={`${data?.getClass?.name || className} Information`} backPageUrl="/teacher-dashboard">
        <ClassInfos classInfo={data?.getClass} loading={loading} />
        <TeacherAnnouncementsRow classId={id} />

        {/* Active Investigations */}
        <ActiveInvestigationsRow
          loading={loadingInvestigations}
          investigations={activeInvestigations?.getInvestigationsByStudentUser}
          error={errorInvestigations?.message}
          page={pageActiveInv}
          setPage={setPageActiveInv}
        />
        <Pagination
          current={pageActiveInv}
          pageSize={pageSize}
          total={hasMorePagesActiveInv ? (pageActiveInv + 1) * pageSize : pageActiveInv * pageSize}
          onChange={(newPage) => handlePageChangeFinishedInv(newPage, DASHBOARD_TYPES.ACTIVE_INVESTIGATIONS)}
          showSizeChanger={false}
          hideOnSinglePage={totalInvestigationsActiveInv <= pageSize}
          style={{ textAlign: 'center', marginTop: 16 }}
          disabled={loading}
        />

        {/* Active Assessments */}
        {(hasTexasSubjectEnabled || hasNonEnterpriseTeacher) && (
          <>
            <ActiveAssessmentsRow loading={loading} assessments={activeAssessments?.getInvestigationsByStudentUser} />
            <Pagination
              current={pageActiveAssess}
              pageSize={pageSize}
              total={hasMorePagesActiveAssessment ? (pageActiveAssess + 1) * pageSize : pageActiveAssess * pageSize}
              onChange={(newPage) => handlePageChangeFinishedInv(newPage, DASHBOARD_TYPES.ACTIVE_ASSESSMENTS)}
              showSizeChanger={false}
              hideOnSinglePage={totalInvestigationsActiveAssessment <= pageSize}
              style={{ textAlign: 'center', marginTop: 16 }}
              disabled={loading}
            />
          </>
        )}

        {/* Finished Investigations */}
        <FinishedInvestigationsRow
          loading={loadingInvestigations}
          investigations={finishedInvestigations?.getInvestigationsByStudentUser}
          error={errorInvestigations?.message}
        />
        <Pagination
          current={pageFinishedInv}
          pageSize={pageSize}
          total={hasMorePagesFinishedInv ? (pageFinishedInv + 1) * pageSize : pageFinishedInv * pageSize}
          onChange={(newPage) => handlePageChangeFinishedInv(newPage, DASHBOARD_TYPES.FINISHED_INVESTIGATIONS)}
          showSizeChanger={false}
          hideOnSinglePage={totalInvestigationsFinishedInv <= pageSize}
          style={{ textAlign: 'center', marginTop: 16 }}
          disabled={loading}
        />



        {(hasTexasSubjectEnabled || hasNonEnterpriseTeacher) && (
          <>
            <FinishedAssessmentsRow assessments={finishedAssessments?.getInvestigationsByStudentUser} loading={loading} />
            <Pagination
              current={pageFinishedAssess}
              pageSize={pageSize}
              total={hasMorePagesFinishedAssessment ? (pageFinishedAssess + 1) * pageSize : pageFinishedAssess * pageSize}
              onChange={(newPage) => handlePageChangeFinishedInv(newPage, DASHBOARD_TYPES.FINISHED_ASSESSMENTS)}
              showSizeChanger={false}
              hideOnSinglePage={totalInvestigationsFinishedAssessment <= pageSize}
              style={{ textAlign: 'center', marginTop: 16 }}
              disabled={loading}
            />

          </>
        )}
      </PageWithTitle>
    </>
  );
};

export default withRouter(StudentClassDetail);
